/**
 * Creatives Page (Refactored)
 * 
 * Main page for viewing ad creative analytics
 * Uses shared MetaConnectionContext
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import { CreativeSummaryCards, CreativeTable, CreativeDetailPanel } from '../components'
import * as api from '../services/api'
import type { CreativeWithPerformance, CreativeSummary, CreativeDetailData } from '../types'
import { MetaConnectionGuard } from '@/shared/components/MetaConnectionGuard'
import { useMetaConnection } from '@/shared/contexts'

// Get date range helper
function getDateRange(days: number): { startDate: string; endDate: string } {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - days)
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  }
}

// ============================================================================
// Creatives Content (Connected State)
// ============================================================================

function CreativesContent() {
  const { connection } = useMetaConnection()
  const [creatives, setCreatives] = useState<CreativeWithPerformance[]>([])
  const [summary, setSummary] = useState<CreativeSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [fullSyncing, setFullSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [datePreset, setDatePreset] = useState<'7D' | '14D' | '30D' | '90D'>('30D')
  const [dateRange, setDateRange] = useState(getDateRange(30))
  const [selectedDetail, setSelectedDetail] = useState<CreativeDetailData | null>(null)

  // Fetch creatives data, then refresh thumbnails in the background
  const fetchCreatives = useCallback(async (range?: { startDate: string; endDate: string }) => {
    const queryRange = range ?? dateRange
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.getCreatives({
        start_date: queryRange.startDate,
        end_date: queryRange.endDate,
      })
      
      const fetchedCreatives = response.creatives || []
      setSummary(response.summary || null)
      // Show creatives immediately with cached thumbnails
      setCreatives(fetchedCreatives)
      setLoading(false)

      // Then refresh thumbnails in the background (non-blocking)
      const creativeIds = fetchedCreatives
        .map((c) => c.creative_id)
        .filter((id): id is string => Boolean(id))

      if (creativeIds.length > 0) {
        try {
          const freshThumbnails = await api.getCreativeThumbnails(creativeIds)
          // Only update if we got real results
          const hasResults = Object.keys(freshThumbnails).length > 0
          if (hasResults) {
            setCreatives((prev) =>
              prev.map((c) => {
                const freshUrl = c.creative_id ? freshThumbnails[c.creative_id] : undefined
                return freshUrl ? { ...c, thumbnail_url: freshUrl } : c
              })
            )
          }
        } catch {
          // Thumbnail refresh is best-effort, don't show error
          console.warn('Failed to refresh thumbnails, using cached URLs')
        }
      }
    } catch (err) {
      console.error('Error fetching creatives:', err)
      setError('Failed to load creatives data')
      setLoading(false)
    }
  }, [dateRange])

  // Smart Sync - fast incremental update with fresh thumbnails
  const handleSmartSync = async () => {
    setSyncing(true)
    setError(null)
    try {
      await api.smartSyncCreatives()
      await fetchCreatives()
    } catch (err) {
      console.error('Error smart syncing creatives:', err)
      setError('Failed to sync creatives')
    } finally {
      setSyncing(false)
    }
  }

  // Full Sync - delete all + re-fetch from Meta
  const handleFullSync = async () => {
    if (!confirm('Full Refresh will delete all cached creatives and re-fetch from Meta. This may take a minute. Continue?')) {
      return
    }
    setFullSyncing(true)
    setError(null)
    try {
      await api.syncCreatives()
      await fetchCreatives()
    } catch (err) {
      console.error('Error full syncing creatives:', err)
      setError('Failed to sync creatives')
    } finally {
      setFullSyncing(false)
    }
  }

  // Handle date preset - pass dates directly to avoid stale closure issues
  function handleDatePreset(preset: '7D' | '14D' | '30D' | '90D') {
    setDatePreset(preset)
    const days = preset === '7D' ? 7 : preset === '14D' ? 14 : preset === '30D' ? 30 : 90
    const newRange = getDateRange(days)
    setDateRange(newRange)
    // Fetch with new dates directly
    fetchCreatives(newRange)
  }

  // Handle creative row click - open detail panel
  const handleCreativeClick = useCallback((creative: CreativeWithPerformance) => {
    const detail = api.buildCreativeDetail(
      creative,
      creatives,
      dateRange.startDate,
      dateRange.endDate,
    )
    setSelectedDetail(detail)
  }, [creatives, dateRange])

  // Initial data load only (not on date change since we call fetchCreatives directly)
  useEffect(() => {
    if (connection.connected) {
      fetchCreatives()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.connected])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-tf-text">Creatives Analytics</h1>
          <p className="text-sm text-tf-text-muted mt-1">
            Monitor performance of your ad creatives across campaigns
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Range Presets */}
          <div className="flex bg-tf-bg-muted rounded-lg p-1">
            {(['7D', '14D', '30D', '90D'] as const).map((preset) => (
              <button
                key={preset}
                onClick={() => handleDatePreset(preset)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${ datePreset === preset ? 'bg-tf-bg-card text-tf-text shadow-sm' : 'text-tf-text-muted hover:bg-tf-bg-card hover:text-tf-text' }`}
              >
                {preset}
              </button>
            ))}
          </div>

          {/* Smart Sync Button */}
          <button
            onClick={handleSmartSync}
            disabled={syncing || fullSyncing}
            title="Refreshes creatives, metrics, and thumbnails from Meta - Fast"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {syncing ? (
              <>
                <Icons.Loader2 className="w-4 h-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Icons.Zap className="w-4 h-4" />
                Smart Sync
              </>
            )}
          </button>

          {/* Full Refresh Button */}
          <button
            onClick={handleFullSync}
            disabled={syncing || fullSyncing}
            title="Deletes all cached data and re-fetches everything from Meta - Slower"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-tf-text-muted bg-tf-bg-muted rounded-lg hover:bg-tf-bg-hover transition-colors disabled:opacity-50"
          >
            {fullSyncing ? (
              <>
                <Icons.Loader2 className="w-4 h-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <Icons.RefreshCw className="w-4 h-4" />
                Full Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {/* Connection Info Banner */}
      {connection.accountId && (
        <div className="flex items-center justify-between p-4 bg-tf-feedback-info-bg border border-primary/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icons.CheckCircle2 className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-medium text-tf-text">
                Connected to {connection.accountName || `Ad Account ${connection.accountId}`}
              </p>
              {connection.lastSyncAt && (
                <p className="text-xs text-tf-text-muted mt-0.5">
                  Last synced: {new Date(connection.lastSyncAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-lg">
          <Icons.AlertCircle className="w-5 h-5 text-tf-feedback-error-text flex-shrink-0" />
          <p className="text-sm text-tf-feedback-error-text">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-tf-feedback-error-text hover:opacity-80">
            <Icons.X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <CreativeSummaryCards summary={summary} loading={loading} />

      {/* Creatives Table */}
      <CreativeTable
        creatives={creatives}
        loading={loading}
        onCreativeClick={handleCreativeClick}
      />

      {/* Creative Detail Panel (slide-over) */}
      {selectedDetail && (
        <CreativeDetailPanel
          data={selectedDetail}
          onClose={() => setSelectedDetail(null)}
        />
      )}
    </div>
  )
}

// ============================================================================
// Main Creatives Page (with Connection Guard)
// ============================================================================

export default function CreativesPage() {
  return (
    <MetaConnectionGuard
      title="Connect Meta Ads Account"
      description="Connect your Meta Ads account to view creative performance analytics."
      icon="Image"
    >
      <CreativesContent />
    </MetaConnectionGuard>
  )
}

